import { Component, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSelectionListChange } from '@angular/material/list';
import { Observable } from 'rxjs';
import { finalize, map, take, tap } from 'rxjs/operators';
import { Client } from 'src/app/models/client.model';
import { PatientEmrData } from 'src/app/models/patient.model';
import { AthenaService } from 'src/app/services/emr/athena.service';
import { PatientDetailService } from 'src/app/services/patient-detail.service';
import { PatientService } from 'src/app/services/patient.service';
import { SnackService } from 'src/app/services/snack.service';

@Component({
  selector: 'app-athena-patient-configuration',
  templateUrl: './athena-patient-configuration.component.html',
  styleUrls: ['./athena-patient-configuration.component.scss'],
})
export class AthenaPatientConfigurationComponent implements OnInit {
  isAthenaEMR$: Observable<boolean>;
  currentClient: Client;
  isSearching = false;
  isSaving = false;
  athenaClinicalProviders: any[];
  providerForm: UntypedFormGroup;

  get isSaveDisabled(): boolean {
    const providerId = this.providerForm?.get('renderProviderId').value;
    const providerName = this.providerForm?.get('renderProviderName').value;
    return providerId == null || providerId === '' || !providerName || this.isSaving;
  }
  constructor(
    private patientDetailService: PatientDetailService,
    private athenaService: AthenaService,
    private snackService: SnackService,
    private fb: UntypedFormBuilder,
    private patientService: PatientService,
    private db: AngularFirestore
  ) {}

  ngOnInit(): void {
    this.isAthenaEMR$ = this.patientService
      .getClientPatient(this.patientService.currentPatientService?.client_responsible_id)
      .pipe(
        take(1),
        tap((client) => (this.currentClient = client)),
        map((client) => client?.emr_data?.emr_name?.includes('athena'))
      );
    this.initProviderForm();
  }

  initProviderForm(): void {
    this.providerForm = this.fb.group({
      last_name: [
        this.patientService.currentPatientService.providers[0]?.name?.split(' ')[1] || '',
        Validators.required,
      ],
      renderProviderId: [
        this.patientService.currentPatientService.emr_data?.renderingproviderid || '',
        { disabled: true },
      ],
      renderProviderName: [
        this.patientService.currentPatientService.emr_data?.renderingprovidername || '',
        { disabled: true },
      ],
    });
  }

  searchAthenaClinicalProvider(): void {
    const lastName = this.providerForm.get('last_name').value;
    this.isSearching = true;
    this.athenaService
      .searchProvidersByName(lastName, this.currentClient.client_id)
      .pipe(finalize(() => (this.isSearching = false)))
      .subscribe({
        next: (response) => {
          this.athenaClinicalProviders = response.providers || [];
        },
        error: (error) => {
          console.error('Error searching Athena providers:', error?.message || 'Internal Error');
          this.snackService.genericSnackBar(
            `${error?.error?.error ?? 'Internal Error'} - ${error?.error?.message ?? 'See logs'}`,
            ['error-snackbar'],
            5000
          );
          this.athenaClinicalProviders = [];
        },
      });
  }

  onProviderSelectionChange(event: MatSelectionListChange): void {
    const selectedOption = event.options[0];
    if (selectedOption?.value) {
      this.selectProvider(selectedOption.value);
    }
  }

  selectProvider(provider: any): void {
    this.providerForm.get('renderProviderId').setValue(provider.providerid);
    this.providerForm.get('renderProviderName').setValue(`${provider.firstname} ${provider.lastname}`);
    this.athenaClinicalProviders = undefined;
  }

  saveAthenaClientProvider(): void {
    const userId = this.patientService.currentPatientService?.user_id;
    if (!userId) {
      this.snackService.genericSnackBar('Unable to save: patient information is missing', ['error-snackbar'], 5000);
      return;
    }

    this.isSaving = true;
    const formValues = this.providerForm.getRawValue();

    this.db
      .collection('users')
      .doc(userId)
      .update({
        'emr_data.renderingproviderid': formValues.renderProviderId,
        'emr_data.renderingprovidername': formValues.renderProviderName,
      })
      .then(
        () => {
          this.isSaving = false;
          const patient = this.patientService.currentPatientService;
          if (!patient.emr_data) {
            patient.emr_data = {} as PatientEmrData;
          }
          patient.emr_data.renderingproviderid = formValues.renderProviderId;
          patient.emr_data.renderingprovidername = formValues.renderProviderName;
          this.snackService.genericSnackBar('Provider saved successfully', ['success-snackbar'], 5000);
        },
        (error) => {
          this.isSaving = false;
          console.error('Error updating Athena provider:', error?.message || 'Internal Error');
          this.snackService.genericSnackBar(
            `${error?.error?.error ?? 'Internal Error'} - ${error?.error?.message ?? 'See logs'}`,
            ['error-snackbar'],
            5000
          );
        }
      );
  }
}
